import React from "react";

export default function About() {
  return (
    <div style={styles.container}>

      {/* HEADER */}
      <h1 style={styles.title}>About FentaSafe</h1>

      <p style={styles.subtitle}>
        AI-Assisted Safety Intelligence for Prevention, Situational Awareness,
        and Human-Supervised Decision Support
      </p>

      <div style={styles.banner}>
        Patent Pending (IN 202641022911) • FentaSafe Technologies, Inc. • Florida Corporation (P26000033569) • K-12 School Safety Innovation • Research Collaboration Ready
      </div>

      {/* INTRO */}
      <div style={styles.sectionCard}>
        <p style={styles.paragraph}>
          FentaSafe is an AI-assisted safety intelligence platform designed to
          support early detection, situational awareness, and proactive
          intervention across schools, communities, public-sector
          organizations, and research environments.
        </p>

        <p style={styles.paragraph}>
          The platform combines environmental sensing, contextual intelligence,
          predictive analytics, adaptive learning, and human-supervised
          decision support to help organizations identify emerging risks before
          incidents escalate.
        </p>

        <p style={styles.paragraph}>
          The core risk classification engine is trained on data — moving beyond
          fixed-threshold logic to detect subtle, combined signal patterns (+16.6%
          accuracy improvement). Real-time decision explanation at 1.7ms average
          latency and memory-driven incident similarity matching replace the static
          rules common in conventional monitoring systems.
        </p>
      </div>

      {/* TOP CARDS */}
      <div style={styles.grid3}>
        <div style={styles.sectionCard}>
          <h2>Company Information</h2>
          <p><strong>Company:</strong> FentaSafe Technologies, Inc.</p>
          <p><strong>Jurisdiction:</strong> State of Florida, USA</p>
          <p><strong>Document Number:</strong> P26000033569</p>
          <p><strong>Verify:</strong> <a href="https://search.sunbiz.org" target="_blank" rel="noreferrer" style={styles.link}>sunbiz.org</a></p>
          <p><strong>Patent Status:</strong> Pending</p>
          <p><strong>Patent Application:</strong> IN 202641022911</p>
          <p><strong>Research Collaboration:</strong> ICMR-NINE (in progress)</p>
        </div>

        <div style={styles.card}>
          <h2>🎯 Our Mission</h2>
          <p>
            To enable institutions and communities with real-time intelligence
            that improves awareness, preparedness, and informed
            decision-making through explainable and privacy-preserving safety
            technologies.
          </p>
          <p style={{marginTop: 12}}>
            Primary focus: K-12 schools and public safety environments where
            early environmental risk detection can prevent life-threatening
            incidents before they escalate.
          </p>
        </div>

        <div style={styles.card}>
          <h2>⚠️ The Public Safety Gap</h2>
          <p>
            Modern safety incidents often develop gradually across
            environmental, behavioral, and contextual signals.
          </p>
          <p style={{marginTop: 12}}>
            Traditional systems frequently detect events after escalation
            rather than supporting early anticipation and coordinated response.
            FentaSafe addresses this gap with a six-agent AI pipeline that
            moves from signal detection to explainable, human-supervised action.
          </p>
        </div>

        <div style={styles.card}>
          <h2>🧠 The FentaSafe Approach</h2>
          <p>
            FentaSafe combines environmental sensing, a trained AI risk classifier, real-time decision explanation engine, memory-driven incident pattern matching, and adaptive learning
            from operator feedback.
          </p>
          <p style={{marginTop: 12}}>
            Rather than generating isolated threshold alerts, the platform
            transforms multi-signal sensor data into contextual, explainable
            intelligence that supports proactive prevention and informed response.
          </p>
        </div>

      </div>

      {/* ARCHITECTURE */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>
          Six-Agent Cognitive Safety Architecture
        </h2>
        <p style={{...styles.centerText, marginBottom: 24, color: "#94a3b8"}}>
          Each agent in the pipeline is independently upgraded, tested, and version-controlled.
          The full pipeline runs end-to-end in under 10ms per sensor reading.
        </p>

        <div style={styles.archGrid}>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>PredictionAgent</div>
            <div style={styles.archDesc}>Trained AI risk classifier (+16.6% accuracy vs. rule-based baseline)</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>ExplainabilityAgent</div>
            <div style={styles.archDesc}>Real-time per-decision explanation engine (1.7ms average latency)</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>MemoryAgent</div>
            <div style={styles.archDesc}>Memory-driven incident similarity matching (94–99% accuracy verified)</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>DecisionAgent</div>
            <div style={styles.archDesc}>Multi-signal auditable decision engine using confidence, explanation, and memory patterns</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>RecommendationAgent</div>
            <div style={styles.archDesc}>Protocol-aware routing based on dominant signal driver (chemical, security, environmental)</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>LearningAgent</div>
            <div style={styles.archDesc}>Adaptive sensitivity learning from operator feedback, persisted across restarts</div>
          </div>
          <div style={styles.archItem}>
            <div style={styles.archLabel}>risk_engine.py</div>
            <div style={styles.archDesc}>Hybrid AI + temporal trend escalation on rolling sensor history</div>
          </div>
        </div>
      </div>

      {/* RESPONSIBLE AI */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>Responsible AI Principles</h2>

        <div style={styles.badgeGrid}>
          <div style={styles.badge}>✓ Human-in-the-Loop Oversight</div>
          <div style={styles.badge}>✓ Real-Time Decision Explainability</div>
          <div style={styles.badge}>✓ Privacy First — Zone-Level Only</div>
          <div style={styles.badge}>✓ No Automated Enforcement</div>
          <div style={styles.badge}>✓ No Facial Recognition</div>
          <div style={styles.badge}>✓ Confidence-Aware Escalation</div>
        </div>
      </div>

      {/* STAKEHOLDERS */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>Who FentaSafe Supports</h2>

        <div style={styles.badgeGrid}>
          <div style={{...styles.badge, ...styles.primaryBadge}}>🏫 K-12 Schools & School Districts</div>
          <div style={styles.badge}>Public Safety Agencies</div>
          <div style={styles.badge}>Emergency Management Organizations</div>
          <div style={styles.badge}>Research Institutions</div>
          <div style={styles.badge}>Community Safety Programs</div>
          <div style={styles.badge}>Technology & Innovation Partners</div>
        </div>
      </div>

      {/* FOUNDER */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>Founder & Lead Architect</h2>

        <p style={styles.centerText}>
          <strong style={{fontSize: "1.15rem"}}>Premarajan Abel</strong><br />
          Founder, President & Lead Architect — FentaSafe Technologies, Inc.
        </p>

        <div style={styles.founderGrid}>
          <div style={styles.founderCard}>
            <h4 style={styles.founderCardTitle}>Experience</h4>
            <p>27+ years in enterprise technology leadership, automation, AI-assisted systems, digital transformation, and public safety innovation across India and the USA.</p>
          </div>
          <div style={styles.founderCard}>
            <h4 style={styles.founderCardTitle}>Technical Focus</h4>
            <p>Multi-agent AI architecture, environmental sensor integration, explainable decision support systems, and real-time risk intelligence platforms.</p>
          </div>
          <div style={styles.founderCard}>
            <h4 style={styles.founderCardTitle}>Research & IP</h4>
            <p>Indian Patent Application No. 202641022911 (Pending) covering the core FentaSafe memory-driven environmental intelligence architecture. Research collaboration in progress with ICMR-NINE (National Institute of NCD Epidemiology).</p>
          </div>
          <div style={styles.founderCard}>
            <h4 style={styles.founderCardTitle}>Vision</h4>
            <p>FentaSafe was created to help organizations transition from reactive response to proactive prevention through environmental intelligence, situational awareness, and human-supervised decision support.</p>
          </div>
        </div>

        <p style={{...styles.centerText, marginTop: 20}}>
          <a href="https://linkedin.com/in/premarajan-abel-13a15349" target="_blank" rel="noreferrer" style={styles.link}>
            LinkedIn Profile →
          </a>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <a href="mailto:premarajan@fentasafe.org" style={styles.link}>
            premarajan@fentasafe.org
          </a>
        </p>
      </div>

      {/* EXECUTIVE SUMMARY */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>Executive Platform Summary</h2>

        <p style={styles.centerText}>
          FentaSafe is an AI-powered environmental intelligence platform designed
          to support situational awareness, environmental threat detection, early
          warning, risk assessment, and emergency alerting for K-12 schools and
          public safety environments.
        </p>
        <p style={{...styles.centerText, marginTop: 12}}>
          The platform's six-agent AI pipeline — built on AI risk classification, real-time decision explanation, memory-driven pattern matching, and persistent adaptive learning — represents a Phase I R&D
          foundation eligible for NSF SBIR funding. School districts can fund
          deployment through the COPS Office School Violence Prevention Program
          (SVPP), providing up to $500,000 per district.
        </p>
      </div>

      {/* FEDERAL FUNDING */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>Federal Funding Alignment</h2>

        <div style={styles.badgeGrid}>
          <div style={{...styles.badge, ...styles.primaryBadge}}>NSF SBIR Phase I (R&D Funding)</div>
          <div style={{...styles.badge, ...styles.primaryBadge}}>COPS SVPP (School Deployment)</div>
          <div style={styles.badge}>School Safety Technology Initiatives</div>
          <div style={styles.badge}>Public Safety Innovation Programs</div>
          <div style={styles.badge}>Research Collaborations</div>
          <div style={styles.badge}>Smart Campus Safety Programs</div>
        </div>
      </div>

      {/* SCHOOL SAFETY FOCUS */}
      <div style={styles.sectionCard}>
        <h2 style={styles.centerHeading}>School Safety Focus</h2>

        <div style={styles.badgeGrid}>
          <div style={styles.badge}>Situational Awareness</div>
          <div style={styles.badge}>Environmental Threat Detection</div>
          <div style={styles.badge}>Early Warning & Alerting</div>
          <div style={styles.badge}>Emergency Response Integration</div>
          <div style={styles.badge}>School Safety Technology Pilots</div>
          <div style={styles.badge}>SVPP-Eligible Deployment</div>
        </div>
      </div>

      {/* DISCLAIMER */}
      <div style={styles.sectionCard}>
        <h2>Research & Evaluation Disclaimer</h2>

        <p>
          FentaSafe demonstrations use simulated and indicative data streams.
          The live platform connects to real sensor hardware via MQTT/FastAPI
          for authorized pilot deployments.
        </p>

        <p style={{marginTop: 12}}>
          The platform is designed to support situational awareness,
          predictive insights, and decision support. It is not intended for
          chemical confirmation, law enforcement actions, individual
          surveillance, facial recognition, or automated enforcement
          activities.
        </p>

        <p style={{marginTop: 12}}>
          All operational decisions remain under human supervision.
          Technology Readiness Level (TRL): 6–7.
        </p>
      </div>

    </div>
  );
}

const styles = {
  banner: {
    background: "#1e3a8a",
    padding: "12px 18px",
    borderRadius: "8px",
    marginBottom: "30px",
    color: "#dbeafe",
    textAlign: "center",
    fontWeight: "600",
  },

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
    color: "#e5e7eb",
  },

  title: {
    textAlign: "center",
    fontSize: "3rem",
    marginBottom: "16px",
  },

  subtitle: {
    textAlign: "center",
    color: "#94a3b8",
    marginBottom: "40px",
    fontSize: "1.1rem",
  },

  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: "20px",
    marginBottom: "30px",
  },

  card: {
    background: "#020617",
    border: "1px solid #1e293b",
    borderRadius: "12px",
    padding: "24px",
    lineHeight: "1.8",
  },

  sectionCard: {
    background: "#020617",
    border: "1px solid #1e293b",
    borderRadius: "12px",
    padding: "40px",
    marginBottom: "24px",
  },

  paragraph: {
    lineHeight: "1.8",
    marginBottom: "15px",
  },

  centerHeading: {
    textAlign: "center",
    marginBottom: "25px",
  },

  centerText: {
    textAlign: "center",
    lineHeight: "1.9",
    maxWidth: "900px",
    margin: "0 auto 12px",
    color: "#cbd5e1",
  },

  archGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
    gap: "18px",
  },

  archItem: {
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: "10px",
    padding: "18px",
    textAlign: "center",
  },

  archLabel: {
    fontWeight: "700",
    color: "#60a5fa",
    marginBottom: 8,
    fontSize: "0.95rem",
  },

  archDesc: {
    fontSize: "0.82rem",
    color: "#94a3b8",
    lineHeight: 1.5,
  },

  badgeGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "15px",
  },

  badge: {
    minWidth: "220px",
    maxWidth: "280px",
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: "10px",
    padding: "16px",
    textAlign: "center",
    fontWeight: "600",
  },

  primaryBadge: {
    border: "1px solid rgba(37, 99, 235, 0.5)",
    background: "rgba(37, 99, 235, 0.10)",
    color: "#93c5fd",
  },

  founderGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
    gap: "16px",
    marginTop: "24px",
  },

  founderCard: {
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: "10px",
    padding: "20px",
    lineHeight: 1.7,
  },

  founderCardTitle: {
    color: "#60a5fa",
    marginBottom: 8,
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  link: {
    color: "#60a5fa",
    textDecoration: "none",
  },
};
